import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Select,
  Input,
  Radio,
  DatePicker,
  message,
  Divider,
} from "antd";
import { useSelector } from "react-redux";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import { API } from "../../../helpers/API/amplifyApi";
import dayjsTZ from "../../../utils/dayjs";
import { pickerToDayBounds } from "../../../utils/timezone/dayBounds";
import { getCompanyTimezone } from "../../../utils/timezone/teamTimezone";

const { TextArea } = Input;
const { Option } = Select;

// Combined action sheet for a single grid cell. Supports two actions:
//   1. Override a single day inside this week.
//   2. Swap this employee's week with another employee in the same dept.
function CellActionModal({
  open,
  onClose,
  schedule,
  weekStart,
  employeeId,
  employees,
  shifts,
  onUpdated,
}) {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [mode, setMode] = useState("override");
  const [overrideDay, setOverrideDay] = useState(null);
  const [overrideShiftId, setOverrideShiftId] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [swapPartnerId, setSwapPartnerId] = useState(null);
  const [saving, setSaving] = useState(false);

  const week = useMemo(
    () => (schedule?.weeks || []).find((w) => w.weekStart === weekStart),
    [schedule, weekStart]
  );

  const employee = useMemo(
    () => employees.find((e) => e.employeeId === employeeId),
    [employees, employeeId]
  );

  const otherEmployeesThisWeek = useMemo(() => {
    if (!week) return [];
    const ids = new Set(
      (week.assignments || [])
        .map((a) => a.employeeId)
        .filter((id) => id && id !== employeeId)
    );
    return employees.filter((e) => ids.has(e.employeeId));
  }, [week, employees, employeeId]);

  useEffect(() => {
    if (!open) return;
    setMode("override");
    setOverrideDay(week?.workingDayList?.[0] || weekStart);
    setOverrideShiftId(schedule?.morningShiftIdSnapshot || null);
    setOverrideReason("");
    setSwapPartnerId(null);
  }, [open, week, weekStart, schedule]);

  const submitOverride = async () => {
    if (!overrideDay || !overrideShiftId) {
      message.warning("Zgjidh ditën dhe turnin.");
      return;
    }
    try {
      setSaving(true);
      const updated = await API.put(
        "shiftSchedules",
        `/shiftSchedules/${encodeURIComponent(schedule.scheduleId)}/override`,
        {
          body: {
            employeeId,
            date: Number(overrideDay),
            shiftId: overrideShiftId,
            reason: overrideReason || null,
          },
        }
      );
      message.success("Override u ruajt.");
      onUpdated?.(updated);
      onClose?.();
    } catch (e) {
      console.error(e);
      message.error("Nuk u ruajt dot.");
    } finally {
      setSaving(false);
    }
  };

  const submitSwap = async () => {
    if (!swapPartnerId) {
      message.warning("Zgjidh punonjësin për shkëmbim.");
      return;
    }
    try {
      setSaving(true);
      const updated = await API.put(
        "shiftSchedules",
        `/shiftSchedules/${encodeURIComponent(schedule.scheduleId)}/swap`,
        {
          body: {
            weekStart: Number(weekStart),
            employeeIdA: employeeId,
            employeeIdB: swapPartnerId,
          },
        }
      );
      message.success("Shkëmbimi u ruajt.");
      onUpdated?.(updated);
      onClose?.();
    } catch (e) {
      console.error(e);
      message.error("Nuk u ruajt dot shkëmbimi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`${employee?.employeeFirstName || ""} ${
        employee?.employeeLastName || ""
      } – java ${dayjsTZ(weekStart).format("DD/MM/YYYY")}`}
      open={open}
      onCancel={onClose}
      centered
      width={640}
      destroyOnHidden
      className={isDarkMode ? "dark darkHeader" : "lightHeader"}
      footer={[
        <MondayButton key="x" className="mondayButtonRed" onClick={onClose}>
          Mbyll
        </MondayButton>,
        <MondayButton
          key="save"
          className="mondayButtonGreen"
          spin={saving}
          onClick={mode === "override" ? submitOverride : submitSwap}
        >
          Ruaj
        </MondayButton>,
      ]}
    >
      <Radio.Group
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        style={{ marginBottom: 12 }}
      >
        <Radio value="override">Override një ditë</Radio>
        <Radio value="swap">Shkëmbim me një punonjës tjetër</Radio>
      </Radio.Group>

      {mode === "override" ? (
        <>
          <div style={{ marginBottom: 8 }}>Zgjidh ditën:</div>
          <DatePicker
            style={{ width: "100%", marginBottom: 12 }}
            value={overrideDay ? dayjsTZ(overrideDay) : null}
            onChange={(d) => setOverrideDay(d ? d.valueOf() : null)}
            disabledDate={(d) => {
              if (!week) return true;
              const day = pickerToDayBounds(d, getCompanyTimezone())?.startMs;
              return day < week.weekStart || day > week.weekEnd;
            }}
          />
          <div style={{ marginBottom: 8 }}>Turni i ri për këtë ditë:</div>
          <Select
            value={overrideShiftId}
            onChange={setOverrideShiftId}
            style={{ width: "100%", marginBottom: 12 }}
          >
            {shifts.map((s) => (
              <Option key={s.shiftId} value={s.shiftId}>
                {s.shiftName}
              </Option>
            ))}
          </Select>
          <div style={{ marginBottom: 8 }}>Arsyeja (opsionale):</div>
          <TextArea
            rows={2}
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="P.sh. mbulim leje"
          />
          <Divider style={{ margin: "12px 0" }} />
          <small style={{ color: "#888" }}>
            Override-i ka prioritet mbi caktimin javor; do të ruhet edhe pas
            rigjenerimit.
          </small>
        </>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            Shkëmbe turnin e këtij punonjësi me:
          </div>
          <Select
            value={swapPartnerId}
            onChange={setSwapPartnerId}
            style={{ width: "100%" }}
            placeholder="Zgjidh kolegun"
            showSearch
            optionFilterProp="children"
          >
            {otherEmployeesThisWeek.map((e) => (
              <Option key={e.employeeId} value={e.employeeId}>
                {e.employeeFirstName} {e.employeeLastName}
              </Option>
            ))}
          </Select>
          <Divider style={{ margin: "12px 0" }} />
          <small style={{ color: "#888" }}>
            Të dy cellat shënohen si të kyçura — algoritmi nuk i ndryshon ato
            në rigjenerim.
          </small>
        </>
      )}
    </Modal>
  );
}

export default CellActionModal;
